import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Divider, Grid, Button } from '@material-ui/core';
import type { Theme } from '@material-ui/core/styles';

import { getRequiredConfig } from '@klpkonsern/appconfig';
import type { AuthLoginRef } from '@klpkonsern/auth-core/routes';
import { AuthLogin } from '@klpkonsern/auth-core/routes';
import { Block, Heading, Typography } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Logo } from '@klp/design/Logo';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { Main } from '@klp/layout';

import { translationsObject } from '../../locales';

const styles = (theme: Theme) => ({
    divider: {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
    },
    logo: {
        maxWidth: 175,
        maxHeight: 175,
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('xs')]: {
            maxWidth: '50%',
        },
    },
    container: {
        textAlign: 'center',
        height: '100%',
        maxWidth: 600,
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    rightIcon: {
        marginLeft: theme.spacing(1),
    },
});

interface IOwnProps {
    mainLogin?: boolean;
}

export const LoginView: React.FC<IOwnProps> = ({ mainLogin }) => {
    const classes = useKlpStyles({}, styles);
    const languages = Object.keys(translationsObject);
    const authLoginRef = useRef<AuthLoginRef>(null);
    const { t, i18n } = useTranslation();

    const setLocale = (lang: string) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('lang', lang);
    };
    const handleClick = useCallback(() => {
        // Call the signin method from AuthLogin component
        if (authLoginRef.current) {
            authLoginRef.current.signIn();
        }
    }, []);

    const getColor = (lang: string) =>
        i18n.language === lang ? 'secondary' : 'primary';

    if (mainLogin) return null;

    return (
        <AuthLogin ref={authLoginRef}>
            <Main
                fullScreen={false}
                withoutSidebar={true}
                centered={true}
                withoutHeader={true}
            >
                <Block marginTop="fat" className={classes.container}>
                    <Logo className={classes.logo} />

                    <Heading
                        klpHeadingLevel="TWO"
                        klpTypographyVariant="gamma"
                        translation={`frontPage.welcome.${getRequiredConfig('WEBAPP_APP_NAME')}`}
                        marginBottom="bulky"
                    />

                    <Grid container={true}>
                        <Grid item={true} xs={12}>
                            <Block>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleClick}
                                    data-testid="LoginButton"
                                    endIcon={
                                        <Illustration
                                            icon={IconName.PROFILE_OUTLINE}
                                            className={classes.rightIcon}
                                        />
                                    }
                                >
                                    {t('auth.login')}
                                </Button>
                            </Block>
                        </Grid>
                        <Grid item={true} xs={12}>
                            <Block marginVertical="compact">
                                {languages.map((lang, index) => (
                                    <React.Fragment key={lang}>
                                        <Link
                                            color={getColor(lang)}
                                            href="#"
                                            onClick={() => setLocale(lang)}
                                        >
                                            {
                                                translationsObject[lang]
                                                    .languageName
                                            }
                                        </Link>
                                        {index < languages.length - 1 && (
                                            <span className={classes.divider}>
                                                ▪
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </Block>
                        </Grid>
                        <Grid item={true} xs={12}>
                            <Block marginVertical="tiny">
                                <Divider className="Auth__Divider" />
                            </Block>
                            <Typography klpTypographyVariant="small">
                                {t('auth.privacyDescription')}
                                <a
                                    href="https://www.klp.no/om-klp/personvern"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t('auth.privacyLinkTitle')}
                                </a>
                            </Typography>
                        </Grid>
                    </Grid>
                </Block>
            </Main>
        </AuthLogin>
    );
};
